import { addToCart, openCartOrGo, goToCart } from '@theme/dert-cart-add';
/**
 * <s-wishlist> — Lists the shopper's saved products inside the wishlist drawer.
 *
 * Renders when its parent <dialog> opens (and again on changes while open), fetching
 * each saved product's `<url>.js` JSON. Rows are cloned from the Liquid-authored
 * <template data-item-template>, so markup and translated labels stay in Liquid.
 *
 * Commands (from descendant buttons with commandfor="<this id>"):
 *   --remove — removes the product named by the source button's data-handle.
 *   --add    — adds the source button's data-variant-id to the cart, closes the drawer
 *              and opens the cart drawer.
 *
 * Child markup: [data-list] (ul), [data-empty], [data-error], template[data-item-template].
 * Data attributes: data-remove-label (contains __TITLE__), data-locale.
 */
import { getItems, removeItem, subscribe } from '@theme/wishlist-store';

class SWishlist extends HTMLElement {
  #ac;
  #renderToken = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const dialog = this.closest('dialog');

    this.addEventListener('command', (event) => {
      const source = event.source;
      if (!(source instanceof HTMLElement)) return;
      if (event.command === '--remove') removeItem(source.dataset.handle);
      if (event.command === '--add') this.#addToCart(source);
    }, { signal });

    dialog?.addEventListener('toggle', (event) => {
      if (event.newState === 'open') this.#render();
    }, { signal });

    subscribe(() => {
      if (!dialog || dialog.open) this.#render();
    }, signal);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #render() {
    const token = ++this.#renderToken;
    const list = this.querySelector('[data-list]');
    const empty = this.querySelector('[data-empty]');
    const error = this.querySelector('[data-error]');
    const template = this.querySelector('template[data-item-template]');
    if (!list || !empty || !template) return;

    const items = getItems();
    if (error) error.hidden = true;
    empty.hidden = items.length > 0;
    if (!items.length) {
      list.replaceChildren();
      return;
    }

    this.setAttribute('aria-busy', 'true');
    const results = await Promise.all(items.map((item) => this.#fetchProduct(item)));
    if (token !== this.#renderToken) return;
    this.removeAttribute('aria-busy');

    const rows = results.filter(Boolean).map(({ item, product }) => this.#buildRow(template, item, product));
    list.replaceChildren(...rows);
    if (error) error.hidden = rows.length === items.length;
    empty.hidden = rows.length > 0 || (error && !error.hidden);
  }

  async #fetchProduct(item) {
    try {
      const response = await fetch(`${item.url.split('?')[0]}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) {
        // A deleted or unpublished product can no longer be shown; forget it quietly.
        if (response.status === 404) removeItem(item.handle);
        return null;
      }
      return { item, product: await response.json() };
    } catch {
      return null;
    }
  }

  #buildRow(template, item, product) {
    const fragment = template.content.cloneNode(true);
    const variant = product.variants?.find((v) => v.available) || product.variants?.[0];
    const singleVariant = (product.variants?.length || 0) <= 1;

    fragment.querySelectorAll('[data-link]').forEach((link) => link.setAttribute('href', item.url));

    const title = fragment.querySelector('[data-title]');
    if (title) title.textContent = product.title;

    const image = fragment.querySelector('[data-image]');
    const imageSrc = product.featured_image;
    if (image instanceof HTMLImageElement) {
      if (imageSrc) {
        const url = new URL(imageSrc, window.location.href);
        url.searchParams.set('width', '200');
        image.src = url.toString();
        image.alt = product.title;
      } else {
        image.remove();
      }
    }

    const price = fragment.querySelector('[data-price]');
    if (price && variant) price.textContent = this.#formatMoney(variant.price);

    const add = fragment.querySelector('[data-add]');
    const options = fragment.querySelector('[data-options]');
    if (singleVariant && variant) {
      add?.setAttribute('data-variant-id', String(variant.id));
      add?.setAttribute('data-url', item.url);
      options?.remove();
    } else {
      add?.remove();
      options?.setAttribute('href', item.url);
      options?.removeAttribute('hidden');
    }

    const remove = fragment.querySelector('[data-remove]');
    if (remove) {
      remove.setAttribute('data-handle', item.handle);
      remove.setAttribute('aria-label', (this.dataset.removeLabel || '').replace('__TITLE__', product.title));
    }

    return fragment;
  }

  #formatMoney(cents) {
    const currency = window.Shopify?.currency?.active;
    const amount = Number(cents) / 100;
    if (!currency) return amount.toFixed(2);
    try {
      return new Intl.NumberFormat(this.dataset.locale || undefined, { style: 'currency', currency }).format(amount);
    } catch {
      return `${amount.toFixed(2)} ${currency}`;
    }
  }

  async #addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;
    if (button.getAttribute('aria-disabled') === 'true') return;

    button.setAttribute('aria-disabled', 'true');
    button.classList.add('opacity-50', 'cursor-wait');
    try {
      const result = await addToCart([{ id: variantId, quantity: 1 }]);
      if (!result.ok) {
        if (button.dataset.url) window.location.assign(button.dataset.url);
      } else if (!result.refreshed) {
        goToCart();
      } else {
        this.closest('dialog')?.close();
        await openCartOrGo();
      }
    } catch {
      if (button.dataset.url) window.location.assign(button.dataset.url);
    } finally {
      button.removeAttribute('aria-disabled');
      button.classList.remove('opacity-50', 'cursor-wait');
    }
  }
}

if (!customElements.get('s-wishlist')) {
  customElements.define('s-wishlist', SWishlist);
}
